// Base fragment strings
export const textPairFragmentStr = `
  fragment textPair on TextPair {
    key
    value
  }
`;

export const labelValuePairFragmentStr = `
  fragment labelValuePair on LabelValuePair {
    label { ...textPair }
    value
  }
`;

// Error Fragment
export const errorFragmentStr = `
  fragment error on Error {
    message {
      key
      value
    }
    status
  }
`;

// Form fragment strings
export const optionValuesFragmentStr = `
  fragment optionValues on Option {
    optionValue
    text {...textPair}
    description {...textPair}
    redirectRoute
  }
`;

export const fieldOptionValueFragmentStr = `
  ${textPairFragmentStr}
  ${optionValuesFragmentStr}

  fragment fieldOptionValues on FieldOptionList {
    label {...textPair}
    error {...textPair}
    options {...optionValues}
  }
`;

export const inputFragmentStr = `
  fragment input on Input {
    error { ...textPair }
    label { ...textPair }
    name
    description { ...textPair }
    requiredField
  }
`;

export const multiSelectFragmentStr = `
  fragment multiselect on MultiSelect {
    description { ...textPair }
    error { ...textPair }
    label { ...textPair }
    name
    optionList { ...optionValues }
  }
`;

export const formGroupFragmentStr = `
  fragment formGroup on FormGroup {
    label { ...textPair }
    error { ...textPair }
    fieldList {
      ... on MultiSelect { ...multiselect }
      ... on Input { ...input }
    }
  }
`;

export const formFragmentStr = `
  fragment form on Form {
    id
    label { ...textPair }
    error { ...textPair }
    formGroupList { ...formGroup }
  }
`;

export const reviewItemFragmentStr = `
  fragment reviewItem on ReviewItem {
    gender
    name
    color
    size
    productId
    productUrl
    imageSrc
    shortSku
  }
`;

export const linkListValuesStr = `
  fragment linkListValues on FieldOptionList {
    label { ...textPair }
    options {
      url: optionValue
      text {...textPair}
      redirectRoute
      description {...textPair}
    }
  }
`;

export const addressFragmentStr = `
  fragment shippingAddressFragment on ShippingAddress {
    accessPointName
    address1
    address2
    country
    formattedCityStateZip
    formattedName
    mapUrl { ...labelValuePair }
  }
`;

// Address fragment for the Address type (different from ShippingAddress)
export const basicAddressFragmentStr = `
  fragment basicAddress on Address {
    id
    address1
    address2
    city
    country
    firstName
    lastName
    name
    postalCode
    state
    province
    isBillingAllowed
    isShippingAllowed
    isDefaultBilling
    isDefaultShipping
  }
`;

export const shippingDetailsFragmentStr = `
  fragment shippingDetails on ShippingDetails {
    title { ...textPair }
    shippedToAddress { ...shippingAddressFragment }
  }
`;

export const billingInfoItemFragmentStr = `
  fragment billingInfoItem on BillingInfoItem {
    paymentIcon
    paymentIconLabel
    cardNumber
    cardExpiration
    paymentType
    email
    paymentURL
    paymentStatus
    billingAddress { ...shippingAddressFragment }
  }
`;

export const billingInfoFragmentStr = `
  fragment billingInfo on BillingInfo {
    title { ...textPair }
    billingInfoList { ...billingInfoItem }
  }
`;

// Vat Details
export const vatDutyDetailsFragment = `
  fragment vatDutyDetails on VatDutyDetails {
    title { ...textPair }
    recipientNotChargedAddlDuties { ...textPair }
    extendedVatDetails {
      vatIncludedInMerchPriceToCountry { ...textPair }
      vatDeductedNonCountryShipments { ...textPair }
      recipientNotLiableForImportDuties { ...textPair }
      vatRefundedForMerhandiseReturns { ...textPair }
    }
    sandhVatNotRefundedMsg { ...textPair }
  }
`;

// Transaction Summary
export const transactionRowFragmentStr = `
  fragment transactionRow on TransactionRow {
    isNegative
    label {...textPair}
    price
  }
`;

export const transactionSummaryFragmentStr = `
  fragment transactionSummary on TransactionSummary {
    isNegative
    itemLabel {...textPair}
    priceLabel {...textPair}
    footer {...transactionRow}
    rows {...transactionRow}
  }
`;

// Next Steps
export const shippingAddressFragmentStr = ` 
  fragment shippingAddress on ShippingAddress {
    accessPointName
    address1
    address2
    country
    formattedCityStateZip
    formattedName
    mapUrl { ...labelValuePair}
  }
`;

export const stepFourFragmentStr = `
  fragment stepFour on StepFour {
    exchangeShipToLocationTitle { ...textPair }
    exchangeShippingLocation { ...shippingAddress }
    exchangeStandardShipping { ...textPair }
    exchangesStandardShippingTimeFrame { ...textPair }
    title { ...labelValuePair}
  }
`;

export const stepTwoFragmentStr = `
  fragment stepTwo on StepTwo {
    carrierLocator { ...textPair }
    dhlReturnNote { ...textPair }
    dhlReturnNumber { ...textPair }
    eligibleDropOffLocations { ...labelValuePair}
    nextStepsQRCodeProvided { ...textPair }
    qrCodePurolatorServiceDetails { ...textPair }
    returnAddress { ...textPair }
    returnAvoidCancellation { ...textPair }
    returnByDate { ...textPair }
    returnLabelGenerated { ...textPair }
    returnLabelProvided { ...textPair }
    returnLabelWaiting { ...textPair }
    returnMethodCustomerShippingShipTo { ...textPair }
    rmaNumber { ...labelValuePair}
    searchQRCodeLocations { ...textPair }
    title { ...labelValuePair }
  }
`;

export const nextStepsFragmentStr = `
  fragment nextSteps on NextSteps {
    title { ...textPair }
    stepOne { ...labelValuePair }
    stepTwo { ...stepTwo }
    stepThree { ...labelValuePair }
    stepFour { ...stepFour }
  }
`;

// Product list
export const productFragmentStr = `
  fragment product on Product {
      collection
      color
      colorSequence
      formattedPrice
      id
      imageUrl
      isCharityItem
      isFinalSaleItem
      isFreeGift
      isGiftBox
      isSample
      name
      price
      productUrl
      size
      sku
      storeItem
      useKicUrl
  }
`;

// RnE Item List
export const rneItemFragmentStr = `
  fragment items on RneItem {
    departmentName
    formattedPrice
    isExchange
    orderLineKey
    price
    product { ...product }
    returnReason
    status
  }
`;

// RnE Product List
export const rneProductsFragmentStr = `
  fragment rneProducts on RneProducts {
    exchangeLabel { ...textPair }
    header { ...textPair }
    includeTags { ...textPair }
    items { ...items }
    returnLabel { ...textPair }
  }
`;

// WSS Payment Details
export const wssPaymentDetailsFragmentStr = `
  fragment wssPaymentDetails on WssPaymentDetails {
    showPaymentUpdateForm
    paymentUpdateNeeded
    paymentInformationHeaderText { ...textPair }
    paymentDeclinedMessage { ...textPair }
    initialErrorBannerMessage { ...textPair }
    orderTotalText { ...textPair }
    amountDueText { ...textPair }
    paymentErrorExplanationTitle { ...textPair }
    paymentErrorExplanationContent { ...textPair }
    updateCardInformationTitle { ...textPair }
    billingAddressTitle { ...textPair }
    billToShippingAddressLabel { ...textPair }
    updateButtonText { ...textPair }
    wssCardLabel { ...textPair }
    grandTotalFormatted
    authAmountFormatted
    currencyPattern
    shippingAddress { ...basicAddress }
    billingCountryList { ...labelValuePair }
  }
`;

// Order Details Main Content Data
export const orderDetailsItemFragmentStr = `
  fragment orderDetailsItem on OrderDetailItems {
    isEmpty
    heading { ...textPair }
    status { ...labelValuePair }
    summary { ...transactionSummary }
    ctaGroup { ...labelValuePair }
    shippingDetails { ...shippingDetails }
    billingInfo { ...billingInfo }
    vatDutyDetails { ...vatDutyDetails }
    nextSteps { ...nextSteps }
    rneProducts { ...rneProducts }
    wssPaymentDetails { ...wssPaymentDetails }
    isRnEOrder
  }
`;

// Original Order Details Product List
export const orderItemsContainerFragmentStr = `
  fragment orderItemsContainer on OrderDetailItems {
    products {
      orderLineKey
      departmentName
      product {
        id
        name
        sku
        storeItem
        price
        formattedPrice
        imageUrl
        color
        size
        productUrl
        collection
        colorSequence
        isSample
        isGiftBox
        isFreeGift
        isCharityItem
        isFinalSaleItem
        useKicUrl
      }
      status
      statusIcon
      isCancellable
      quantity
      totalPrice
      text {
        item { ...textPair }
        storeItemNumber { ...textPair }
        sku { ...textPair }
        size { ...textPair }
        color { ...textPair }
        finalSaleMessage { ...textPair }
        cancelItem { ...textPair }
        returnForRefund { ...textPair }
        returnThisItem { ...textPair }
        exchangeButtonLabel { ...textPair }
        reserveAgain { ...textPair }
        itemsInReservation { ...textPair }
      }
    }
    packages {
      packageNumber
      status
      statusIcon
      trackingNumber
      trackingURL
      orderLineKeys
      packageSummaryData { ...transactionSummary }
      packageTexts {
        packageHeaderText
        trackPackageText
      }
    }
  }
`;

// RnE Order Details Product List
export const returnExchangeTextFragmentStr = `
  fragment returnExchangeText on ReturnExchangeText {
    return { ...textPair }
    exchange { ...textPair }
  }
`;

export const returnExchangeItemFragmentStr = `
  fragment returnExchangeItem on ReturnExchangeItem {
    product {
      id
      name
      sku
      price
      formattedPrice
      imageUrl
      color
      size
      productUrl
      isSample
      isGiftBox
      isFreeGift
      isCharityItem
      isFinalSaleItem
      useKicUrl
    }
    isExchange
    returnReason
    price
    formattedPrice
    quantity
    orderLineKey
  }
`;

export const returnExchangeItemsContainerFragmentStr = `
  fragment returnExchangeItemsContainer on ReturnExchangeItemsContainer {
    items { ...returnExchangeItem }
    headerText { ...textPair }
    subHeaderText { ...textPair }
    returnExchangeText { ...returnExchangeText }
    text {
      sku { ...textPair }
      storeItemNumber { ...textPair }
      finalSaleMessage { ...textPair }
    }
  }
`;

export const wssDetailsQueryStr = `
  ${textPairFragmentStr}
  ${labelValuePairFragmentStr}
  
  query GetWssDetails($orderNumber: ID!) {
    orders: orderDetails(orderId: $orderNumber, email: "") { # Email can be empty for this query with order number
      orderDetailItems {
        wssPaymentDetails {
          showPaymentUpdateForm
          paymentUpdateNeeded
          paymentDeclinedMessage { ...textPair }
          initialErrorBannerMessage { ...textPair }
          orderTotalText { ...textPair }
          amountDueText { ...textPair }
          paymentErrorExplanationTitle { ...textPair }
          paymentErrorExplanationContent { ...textPair }
          updateCardInformationTitle { ...textPair }
          billingAddressTitle { ...textPair }
          billToShippingAddressLabel { ...textPair }
          updateButtonText { ...textPair }
          grandTotalFormatted
          authAmountFormatted
          currencyPattern
          shippingAddress {
            address1
            address2
            city
            state
            postalCode
            country
            firstName
            lastName
          }
          billingCountryList { ...labelValuePair }
        }
      }
    }
  }
`;
